import React, { useEffect, useState } from 'react';
import Card from '../../components/ui/Card';
import Btn from '../../components/ui/Btn';
import Badge from '../../components/ui/Badge';
import Secao from '../../components/ui/Secao';
import { Input, Select } from '../../components/ui/Input';
import { estoqueApi, depositosApi } from '../../api/estoque';
import { COLORS } from '../../theme';
import { useTituloPagina } from '../../context/PageTitleContext';
import { useAuth } from '../../context/AuthContext';

const TIPOS_MOV = [
  { value: 'ajuste_positivo', label: 'Ajuste Positivo' },
  { value: 'ajuste_negativo', label: 'Ajuste Negativo' }
];

const TIPO_LABEL = {
  entrada: 'Entrada', saida: 'Saída', transferencia: 'Transferência',
  ajuste_positivo: 'Ajuste (+)', ajuste_negativo: 'Ajuste (−)',
  producao_consumo: 'Consumo (Produção)', producao_entrada: 'Entrada (Produção)'
};
const TIPOS_ENTRADA = ['entrada', 'ajuste_positivo', 'producao_entrada'];
const LIMITE_MOV = 15;

const FILTROS_VAZIOS = { data_de: '', data_ate: '', tipo: '', quantidade: '', custo_unitario: '', documento: '', motivo: '' };
const OPCOES_TIPO_FILTRO = [{ value: '', label: 'Todos' }, ...Object.entries(TIPO_LABEL).map(([value, label]) => ({ value, label }))];

export default function EstoqueItemDetail({ produtoId, onVoltar }) {
  const { perfil } = useAuth();
  const podeAjustar = ['admin', 'gestor'].includes(perfil);
  const [produto, setProduto] = useState(null);
  const [saldos, setSaldos] = useState([]);
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [depositos, setDepositos] = useState([]);
  const [formAberto, setFormAberto] = useState(false);
  const [form, setForm] = useState({ tipo: 'ajuste_positivo', quantidade: '', deposito_id: '', motivo: '' });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const [filtrosMov, setFiltrosMov] = useState(FILTROS_VAZIOS);
  const [paginaMov, setPaginaMov] = useState(1);
  const [temMaisMov, setTemMaisMov] = useState(false);
  const [carregandoMov, setCarregandoMov] = useState(false);

  useTituloPagina(produto ? `Estoque — ${produto.nome}` : 'Estoque');

  const carregar = () => {
    estoqueApi.resumo({ produto_id: produtoId }).then(lista => setProduto(lista[0] || null)).catch(() => {});
    estoqueApi.saldos({ produto_id: produtoId }).then(setSaldos).catch(() => {});
  };

  const carregarMovimentacoes = (pagina, acumular, filtros) => {
    setCarregandoMov(true);
    const params = { produto_id: produtoId, limite: LIMITE_MOV, pagina };
    Object.entries(filtros).forEach(([k, v]) => { if (v) params[k] = v; });
    estoqueApi.movimentacoes(params)
      .then(lista => {
        setMovimentacoes(prev => acumular ? [...prev, ...lista] : lista);
        setTemMaisMov(lista.length === LIMITE_MOV);
      })
      .catch(() => {})
      .finally(() => setCarregandoMov(false));
  };

  useEffect(() => { carregar(); depositosApi.listar().then(setDepositos).catch(() => {}); }, [produtoId]); // eslint-disable-line

  useEffect(() => {
    const t = setTimeout(() => { setPaginaMov(1); carregarMovimentacoes(1, false, filtrosMov); }, 350);
    return () => clearTimeout(t);
  }, [produtoId, filtrosMov]); // eslint-disable-line

  const verMaisMov = () => {
    const proxima = paginaMov + 1;
    setPaginaMov(proxima);
    carregarMovimentacoes(proxima, true, filtrosMov);
  };

  const atualizarFiltroMov = (campo, valor) => setFiltrosMov(f => ({ ...f, [campo]: valor }));

  const totalEstoque = saldos.reduce((s, r) => s + Number(r.quantidade), 0);
  const opcoesDepositos = [{ value: '', label: 'Selecione...' }, ...depositos.map(d => ({ value: d.id, label: d.nome }))];

  const salvarMovimentacao = async () => {
    if (!form.quantidade || !form.deposito_id) { setErro('Preencha quantidade e depósito'); return; }
    if (!form.motivo.trim()) { setErro('Informe o motivo do ajuste'); return; }
    setSalvando(true);
    setErro('');
    try {
      const ehEntrada = TIPOS_ENTRADA.includes(form.tipo);
      await estoqueApi.registrarMovimentacao({
        produto_id: produtoId,
        tipo: form.tipo,
        quantidade: form.quantidade,
        motivo: form.motivo || null,
        ...(ehEntrada ? { deposito_destino_id: form.deposito_id } : { deposito_origem_id: form.deposito_id })
      });
      setForm({ tipo: 'ajuste_positivo', quantidade: '', deposito_id: '', motivo: '' });
      setFormAberto(false);
      carregar();
      setPaginaMov(1);
      carregarMovimentacoes(1, false, filtrosMov);
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  if (!produto) return null;

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, borderBottom: `1px solid ${COLORS.border}`, paddingBottom: 14 }}>
          <div>
            <strong style={{ fontSize: 15 }}>{produto.codigo} — {produto.nome}</strong>
            <div style={{ fontSize: 12, color: COLORS.textMid }}>Código Fabricante: {produto.codigo_fabricante || '-'}</div>
          </div>
          <Btn variante="ghost" onClick={onVoltar}>Voltar</Btn>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div style={{ background: COLORS.bg, borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 11, color: COLORS.textLight, textTransform: 'uppercase', fontWeight: 700 }}>Total em Estoque</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{totalEstoque}</div>
          </div>
          <div style={{ background: COLORS.warningBg, borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 11, color: COLORS.warning, textTransform: 'uppercase', fontWeight: 700 }}>Reservado (Pedidos)</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: COLORS.warning }}>
              {Number(produto.total_reservado || 0)}
            </div>
          </div>
          <div style={{ background: COLORS.bg, borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 11, color: COLORS.textLight, textTransform: 'uppercase', fontWeight: 700 }}>Disponível</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{Number(produto.total_disponivel ?? totalEstoque)}</div>
          </div>
          <div style={{ background: COLORS.primaryBg, borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 11, color: COLORS.primary, textTransform: 'uppercase', fontWeight: 700 }}>Total Esperado (OCs em aberto)</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: COLORS.primary }}>
              {Number(produto.total_esperado) > 0 ? `+${Number(produto.total_esperado)}` : '0'}
            </div>
          </div>
        </div>

        <Secao titulo="Saldo por Depósito" acao={podeAjustar ? <Btn tamanho="sm" variante="secondary" onClick={() => setFormAberto(a => !a)}>{formAberto ? 'Cancelar' : '+ Ajuste Manual'}</Btn> : null}>
          {formAberto && podeAjustar && (
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1.5fr auto', gap: 8, alignItems: 'end', marginBottom: 14, padding: 10, background: COLORS.bg, borderRadius: 8 }}>
              <Select label="Tipo" options={TIPOS_MOV} value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))} />
              <Input label="Quantidade" type="number" step="0.001" value={form.quantidade} onChange={e => setForm(f => ({ ...f, quantidade: e.target.value }))} />
              <Select label="Depósito" options={opcoesDepositos} value={form.deposito_id} onChange={e => setForm(f => ({ ...f, deposito_id: e.target.value }))} />
              <Input label="Motivo (obrigatório)" value={form.motivo} onChange={e => setForm(f => ({ ...f, motivo: e.target.value }))} required />
              <Btn tamanho="sm" onClick={salvarMovimentacao} disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar'}</Btn>
            </div>
          )}
          {erro && <p style={{ color: COLORS.danger, fontSize: 13 }}>{erro}</p>}

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', background: COLORS.bg }}>
                <th style={{ padding: 8 }}>Depósito</th>
                <th style={{ padding: 8 }}>Lote</th>
                <th style={{ padding: 8 }}>Quantidade</th>
                <th style={{ padding: 8 }}>Atualizado em</th>
              </tr>
            </thead>
            <tbody>
              {saldos.map(s => (
                <tr key={s.id} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                  <td style={{ padding: 8 }}>{s.deposito_nome}</td>
                  <td style={{ padding: 8 }}>{s.lote_id ? s.lote_id : '-'}</td>
                  <td style={{ padding: 8 }}>{s.quantidade}</td>
                  <td style={{ padding: 8 }}>{s.atualizado_em}</td>
                </tr>
              ))}
              {saldos.length === 0 && <tr><td colSpan={4} style={{ padding: 14, textAlign: 'center', color: COLORS.textMid }}>Sem saldo registrado.</td></tr>}
            </tbody>
          </table>
        </Secao>

        <div style={{ height: 14 }} />

        <Secao titulo="Entradas e Saídas">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', background: COLORS.bg }}>
                <th style={{ padding: '8px 8px 4px' }}>Data</th>
                <th style={{ padding: '8px 8px 4px' }}>Tipo</th>
                <th style={{ padding: '8px 8px 4px' }}>Quantidade</th>
                <th style={{ padding: '8px 8px 4px' }}>Custo Unit.</th>
                <th style={{ padding: '8px 8px 4px' }}>Documento</th>
                <th style={{ padding: '8px 8px 4px' }}>Motivo</th>
              </tr>
              <tr style={{ background: COLORS.bg }}>
                <th style={{ padding: '0 8px 8px' }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <input type="date" value={filtrosMov.data_de} onChange={e => atualizarFiltroMov('data_de', e.target.value)}
                      style={{ width: '100%', fontSize: 11, padding: '4px 5px', border: `1px solid ${COLORS.border}`, borderRadius: 5, background: '#fff' }} />
                    <input type="date" value={filtrosMov.data_ate} onChange={e => atualizarFiltroMov('data_ate', e.target.value)}
                      style={{ width: '100%', fontSize: 11, padding: '4px 5px', border: `1px solid ${COLORS.border}`, borderRadius: 5, background: '#fff' }} />
                  </div>
                </th>
                <th style={{ padding: '0 8px 8px' }}>
                  <select value={filtrosMov.tipo} onChange={e => atualizarFiltroMov('tipo', e.target.value)}
                    style={{ width: '100%', fontSize: 11, padding: '4px 5px', border: `1px solid ${COLORS.border}`, borderRadius: 5, background: '#fff' }}>
                    {OPCOES_TIPO_FILTRO.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </th>
                <th style={{ padding: '0 8px 8px' }}>
                  <input placeholder="Filtrar..." value={filtrosMov.quantidade} onChange={e => atualizarFiltroMov('quantidade', e.target.value)}
                    style={{ width: '100%', fontSize: 11, padding: '4px 5px', border: `1px solid ${COLORS.border}`, borderRadius: 5, background: '#fff', boxSizing: 'border-box' }} />
                </th>
                <th style={{ padding: '0 8px 8px' }}>
                  <input placeholder="Filtrar..." value={filtrosMov.custo_unitario} onChange={e => atualizarFiltroMov('custo_unitario', e.target.value)}
                    style={{ width: '100%', fontSize: 11, padding: '4px 5px', border: `1px solid ${COLORS.border}`, borderRadius: 5, background: '#fff', boxSizing: 'border-box' }} />
                </th>
                <th style={{ padding: '0 8px 8px' }}>
                  <input placeholder="Filtrar..." value={filtrosMov.documento} onChange={e => atualizarFiltroMov('documento', e.target.value)}
                    style={{ width: '100%', fontSize: 11, padding: '4px 5px', border: `1px solid ${COLORS.border}`, borderRadius: 5, background: '#fff', boxSizing: 'border-box' }} />
                </th>
                <th style={{ padding: '0 8px 8px' }}>
                  <input placeholder="Filtrar..." value={filtrosMov.motivo} onChange={e => atualizarFiltroMov('motivo', e.target.value)}
                    style={{ width: '100%', fontSize: 11, padding: '4px 5px', border: `1px solid ${COLORS.border}`, borderRadius: 5, background: '#fff', boxSizing: 'border-box' }} />
                </th>
              </tr>
            </thead>
            <tbody>
              {movimentacoes.map(m => (
                <tr key={m.id} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                  <td style={{ padding: 8 }}>{new Date(m.data_movimentacao).toLocaleString('pt-BR')}</td>
                  <td style={{ padding: 8 }}>
                    <Badge tom={TIPOS_ENTRADA.includes(m.tipo) ? 'sucesso' : 'perigo'}>{TIPO_LABEL[m.tipo] || m.tipo}</Badge>
                  </td>
                  <td style={{ padding: 8 }}>{m.quantidade}</td>
                  <td style={{ padding: 8 }}>{m.custo_unitario ? `R$ ${Number(m.custo_unitario).toFixed(2)}` : '-'}</td>
                  <td style={{ padding: 8 }}>{m.documento_tipo ? `${m.documento_tipo} ${m.documento_numero || ''}` : '-'}</td>
                  <td style={{ padding: 8 }}>{m.motivo || '-'}</td>
                </tr>
              ))}
              {!carregandoMov && movimentacoes.length === 0 && <tr><td colSpan={6} style={{ padding: 14, textAlign: 'center', color: COLORS.textMid }}>Nenhuma movimentação encontrada.</td></tr>}
            </tbody>
          </table>
          {temMaisMov && (
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <Btn variante="ghost" tamanho="sm" onClick={verMaisMov} disabled={carregandoMov}>{carregandoMov ? 'Carregando...' : 'Ver mais'}</Btn>
            </div>
          )}
        </Secao>
      </Card>
    </div>
  );
}
